import { DropzoneArea } from "@skrice.studio/material-ui-dropzone-mui";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    Input,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import { createForm } from "rc-form";
import * as React from "react";
import { ItemTemplate, TemplateItem } from "../../../../shared/items/constants";

import { Item } from ".";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

interface CreateItemSate {
    modalVisible: boolean;
    creatingFile: boolean;
    error: null | string;
    files: any[];
    editMode: boolean;
}

interface CreateItemProps {
    buttonText?: string | Element;
    buttonIcon?: boolean;
    template: ItemTemplate;
    refreshParent(): void;
    modalTitle: string;
    item?: Item;
    lastItemId?: null | number;
    form: any;
    classes: any;
}

class ManageInventoryItemMain extends React.Component<CreateItemProps, CreateItemSate> {

    constructor(props) {
        super(props);
        // List with grid items
        this.state = {
            // Flags
            modalVisible: false,
            error: null,
            creatingFile: false,
            files: [],

            editMode: !!this.props.item,
        };
    }

    /**
     * Callback function for create button
     * Calling this function a modal will be shown
     */
    showModal = () => {
        this.setState({
            modalVisible: true,
        });
    };

    handleModalCancel = () => {
        this.setState({
            modalVisible: false,
            error: null,
            files: [],
        }, () => {
            // Reset form values
            this.props.form.resetFields();
        });
    };

    handleModalCreate = () => {
        const self = this;

        this.props.form.validateFields((err, values) => {
            if (err) {
                return;
            }

            let endpoint;
            const formData = new FormData();

            formData.append("type", this.props.template.type);

            // If upload form
            if (this.props.template.template.some(item => item.key === "file")) {
                this.state.files.forEach((file) => {
                    formData.append("file", file);
                });
            }

            this.props.template.template.forEach(item => {
                const formattedValue = item.format && item.format === "integer" ?
                    parseInt(values[item.key], 10) :
                    values[item.key];

                formData.append(item.key, formattedValue);
            })

            if (this.state.editMode && this.props.item) {
                formData.append("item_id", this.props.item.item_id);

                endpoint = axios.put(`${Config.getInstance.apiUrl}/api/items`, formData, {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                });
            } else {
                endpoint = axios.post(`${Config.getInstance.apiUrl}/api/items`, formData, {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                });
            }

            this.setState({ creatingFile: true }, () => {
                endpoint.then((result) => {
                    // Reset form fields
                    self.props.form.resetFields();

                    // Otherwise load and show a list with applications
                    return this.setState({
                        creatingFile: false,
                        modalVisible: false,
                        files: [],
                    }, () => {
                        // Refresh parent component
                        this.props.refreshParent();
                    });
                }).catch(_ => {
                    PopupMessage.error("Something went wrong!");

                    this.setState({
                        creatingFile: false,
                        files: [],
                    });
                });
            });
        });
    };

    chooseFile = (files) => {
        this.setState({files});
    };

    /**
     * Render template item react component
     *
     * @param {TemplateItem} templateItem
     * @returns
     * @memberof ManageInventoryItemMain
     */
    renderTemplateItem(templateItem: TemplateItem) {
        const {getFieldDecorator} = this.props.form;

        // this is the initial value of the item
        const initialValue = templateItem.key === "game_id" ?
            (this.props.item ? this.props.item[templateItem.key] : (this.props.lastItemId ? this.props.lastItemId : "")) :
            this.props.item ? this.props.item[templateItem.key] : "";

        return <Grid item xs={6} spacing={2}>
            <FormControl style={{marginTop: 15, width: "100%"}} className={templateItem.type === "select" ? "with-select" : ""}>
                {templateItem.type !== "input" && <InputLabel id="item-id-label">{templateItem.label}</InputLabel>}

                {getFieldDecorator(templateItem.key, {
                    rules: [{
                        required: true,
                        message: `Please input ${templateItem.key}`,
                    }],
                    initialValue
                })(
                    this.renderTemplateItemInput(templateItem)
                )}
            </FormControl>
        </Grid>
    }

    /**
     * Render template item input
     *
     * @param {TemplateItem} templateItem
     * @returns
     * @memberof ManageInventoryItemMain
     */
    renderTemplateItemInput(templateItem: TemplateItem) {
        // Exception
        if (templateItem.key === "game_id") {
            return <TextField label={templateItem.label} style={{width: "100%"}}  variant="outlined" size="small" disabled={!!this.props.item}/>;
        }

        switch (templateItem.type) {
            case "input":
                return <TextField
                    label={templateItem.label}
                    style={{width: "100%"}}
                    variant="outlined"
                    size="small"
                    disabled={templateItem.disabled ?? false}
                />;
            case "file":
                return <DropzoneArea
                    onChange={this.chooseFile}
                    acceptedFiles={["image/jpeg", "image/png", "image/jpg"]}
                    showFileNamesInPreview={true}
                    filesLimit={1}
                    showFileNames={true}
                />;
            case "select":
                return <Select style={{width: "100%"}} size="small" label={templateItem.label}>
                    {templateItem.values.map(valueItem => (
                        <MenuItem value={valueItem.key}>{valueItem.label}</MenuItem>
                    ))}
                </Select>;
            default:
                return null;
        }
    }

    render() {
        const {classes} = this.props;

        return (
            <div>
                {this.props.buttonIcon
                    ? <span onClick={this.showModal} style={{cursor: "pointer"}}>
                        {this.props.buttonText}
                    </span>
                    : <Button disableElevation color="primary" onClick={this.showModal} size="small" variant="contained">
                        {this.props.buttonText}
                    </Button>
                }

                <Dialog onClose={this.handleModalCancel} aria-labelledby="simple-dialog-title"
                        open={this.state.modalVisible} fullWidth={true} maxWidth="md">
                    <DialogTitle id="simple-dialog-title">{this.state.editMode ? `Edit ${this.props.item.name}` : "Add item"}</DialogTitle>

                    <DialogContent>
                        <Grid container spacing={2}>
                            {this.props.template.template.map(templateItem => this.renderTemplateItem(templateItem))}
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            disableElevation
                            onClick={this.handleModalCancel}
                            disabled={this.state.creatingFile}
                            size="small"
                            variant="outlined"
                        >
                            Cancel
                        </Button>

                        <div className={classes.wrapper}>
                            <Button disableElevation
                                onClick={this.handleModalCreate}
                                disabled={this.state.creatingFile}
                                color="primary"
                                className={classes.loadingButton}
                                size="small"
                                variant="contained"
                            >
                                {this.state.editMode ? "Edit" : "Add"}
                            </Button>

                            {this.state.creatingFile &&
                                <CircularProgress size={24} className={classes.buttonProgress}/>
                            }
                        </div>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

const styles: any = theme => ({
    loadingButton: {
        width: "100%",
    },
    wrapper: {
        margin: 8,
        position: "relative",
        marginTop: 10,
        width: 150,
    },
    buttonProgress: {
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
});

export const ManageInventoryItem = withStyles(styles)(createForm()(ManageInventoryItemMain));
